import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCategories } from "@/hooks/useCategories";
import { CategoryIcon } from "@/components/CategoryIcon";
import { StatusBadge } from "@/components/StatusBadge";
import { formatTimeAgo } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Zap, ChevronRight, BadgeCheck, Star } from "lucide-react";

export function Home() {
  const { user } = useAuth();
  const cats = useCategories();

  const { data: publicRequests, isLoading: loadingReqs } = useQuery({
    queryKey: ["public-requests"],
    queryFn: async () => (await api.get("/public/requests")).data,
  });

  const { data: featuredPros } = useQuery({
    queryKey: ["featured-pros"],
    queryFn: async () => (await api.get("/public/featured-pros")).data,
  });

  return (
    <div className="px-4 sm:px-6 pt-6 sm:pt-10 space-y-8 sm:space-y-14" data-testid="home-page">
      <section className="grid gap-8 lg:grid-cols-2 lg:gap-14 lg:items-center">
        <div className="space-y-4 sm:space-y-6">
          <div className="inline-flex items-center gap-1.5 bg-whoo-soft text-whoo text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
            <Zap className="w-3.5 h-3.5" /> Disponibili ora
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
            Un professionista,<br />quando ti serve.<br /><span className="text-whoo">Subito.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
            Idraulici, elettricisti, fabbri e soccorso stradale: pubblica la richiesta e ricevi offerte in pochi minuti.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <Link to={user ? (user.role === "professional" ? "/pro/feed" : "/richieste/nuova") : "/register"}>
              <Button data-testid="hero-cta-btn" className="w-full sm:w-auto sm:px-10 h-14 rounded-full bg-whoo hover:bg-whoo-dark text-white font-heading font-semibold text-base active:scale-[0.98] transition-transform shadow-lg shadow-orange-500/25">
                {user?.role === "professional" ? "Trova richieste" : "Richiedi un intervento"}
              </Button>
            </Link>
            {!user && (
              <Link to="/register?role=professional">
                <Button data-testid="hero-pro-cta" variant="outline" className="w-full sm:w-auto sm:px-10 h-14 rounded-full border-2 font-heading font-semibold text-base active:scale-[0.98] transition-transform">
                  Diventa professionista
                </Button>
              </Link>
            )}
          </div>
        </div>
        <div className="relative hidden lg:block" data-testid="hero-visual">
          <img src="https://images.pexels.com/photos/17514177/pexels-photo-17514177.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Intervento a domicilio" className="w-full h-[420px] object-cover rounded-3xl shadow-xl" />
          <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-lg border border-slate-100 px-5 py-4 flex items-center gap-3">
            <span className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse shrink-0" />
            <div>
              <p className="font-heading font-bold text-slate-900" data-testid="hero-live-count">{publicRequests?.length ?? 0} richieste live</p>
              <p className="text-xs text-slate-500">Professionisti disponibili ora nella tua zona</p>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-heading text-lg sm:text-2xl font-semibold text-slate-900" data-testid="categories-title">Categorie</h2>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3" data-testid="categories-grid">
          {cats.map((c) => (
            <Link
              key={c.key || c.id}
              to={user ? `/richieste/nuova?categoria=${c.key || c.id}` : "/register"}
              data-testid={`category-${c.key || c.id}`}
              className="flex flex-col items-center justify-start gap-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-3 min-h-[104px] hover:border-whoo/40 hover:shadow-md transition-shadow active:scale-95"
            >
              <span className="w-10 h-10 rounded-xl bg-whoo-soft text-whoo flex items-center justify-center shrink-0">
                <CategoryIcon icon={c.icon} className="w-5 h-5" />
              </span>
              <span className="text-[11px] font-semibold text-slate-700 text-center leading-tight line-clamp-2">{c.label}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-heading text-lg sm:text-2xl font-semibold text-slate-900" data-testid="featured-pros-title">Disponibili ora</h2>
        {featuredPros?.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {featuredPros.map((p) => (
              <div key={p.id || p.user_id} data-testid={`featured-pro-${p.id || p.user_id}`} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-volt-soft text-volt flex items-center justify-center font-heading font-bold text-lg">
                  {(p.name || "P")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="font-semibold text-slate-900 truncate">{p.name}</p>
                    <BadgeCheck className="w-4 h-4 text-volt shrink-0" />
                  </div>
                  <p className="text-xs text-slate-500 capitalize">{(p.categories || []).join(" · ")}</p>
                </div>
                {p.rating > 0 && (
                  <span className="flex items-center gap-1 text-sm font-semibold text-slate-700">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {p.rating.toFixed(1)}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-dashed border-slate-200 p-6 text-center" data-testid="no-featured-pros">
            <p className="text-sm text-slate-500">I professionisti "Disponibile ora" appariranno qui in tempo reale.</p>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg sm:text-2xl font-semibold text-slate-900" data-testid="public-requests-title">Richieste live</h2>
          <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 uppercase tracking-wider"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />Live</span>
        </div>
        {loadingReqs ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-3xl" />)}</div>
        ) : publicRequests?.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="public-requests-list">
            {publicRequests.map((r) => (
              <div key={r.id} data-testid={`public-request-${r.id}`} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-9 h-9 rounded-xl bg-whoo-soft text-whoo flex items-center justify-center shrink-0">
                      <CategoryIcon icon={r.category} className="w-4 h-4" />
                    </span>
                    <p className="font-semibold text-slate-900 text-sm leading-snug">{r.title}</p>
                  </div>
                  <StatusBadge status={r.status} testid={`request-status-${r.id}`} />
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{r.zone || "Zona non specificata"}</span>
                  <span>{formatTimeAgo(r.created_at)}</span>
                  {r.urgent && <span className="text-whoo font-bold flex items-center gap-1"><Zap className="w-3.5 h-3.5" />Urgente</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500" data-testid="no-public-requests">Nessuna richiesta pubblica al momento.</p>
        )}
      </section>

      {!user && (
        <section className="bg-slate-900 rounded-3xl p-6 sm:p-12 space-y-3 sm:space-y-5 text-left" data-testid="pro-cta-section">
          <h3 className="font-heading text-xl sm:text-3xl font-semibold text-white">Sei un professionista?</h3>
          <p className="text-sm text-slate-300 leading-relaxed">Attiva "Disponibile ora" e ricevi richieste di lavoro vicino a te, in tempo reale.</p>
          <Link to="/register?role=professional">
            <Button data-testid="pro-register-cta" variant="secondary" className="rounded-full bg-white text-slate-900 hover:bg-slate-100 font-semibold active:scale-95 transition-transform">
              Diventa professionista <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </section>
      )}
    </div>
  );
}
